import React from 'react';
import { useForm, FormProvider, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { BasicFields } from './form/BasicFields';
import { CapacityFields } from './form/CapacityFields';
import { DateFields } from './form/DateFields';
import SeasonField from './form/SeasonField';
import { ScheduleFields } from './form/ScheduleFields';
import { dollarsToCents, centsToDollars } from '@/utils/money';
import { Tooltip } from '@/components/ui/tooltip';
import { 
  BookOpen, 
  MapPin, 
  Users, 
  DollarSign, 
  Calendar, 
  Info, 
  Settings,
  Clock,
  Tag,
  Shirt,
  Award,
  CheckCircle2,
  Save,
  X,
  CreditCard
} from 'lucide-react';

// Define the schema for class form validation
export const ClassFormSchema = z.object({
  name: z.string().min(2, "Class name must be at least 2 characters"),
  description: z.string().optional(),
  location_id: z.string().optional(), // Can be an ID, 'none', 'virtual', or 'offsite'
  capacity: z.number().min(1, "Capacity must be at least 1").default(1),
  season_id: z.string().nullable().optional(), // Optional season assignment
  billing_model: z.enum(['monthly', 'per_session', 'weekly', 'per_class', 'annual'], { required_error: 'Billing model is required' }),
  price_dollars: z.string().refine(
    (val) => val === '' || /^\d+(\.\d{1,2})?$/.test(val), 
    'Enter a valid dollar amount'
  ),
  schedule_type: z.enum(['specific_dates', 'recurring_pattern']).default('recurring_pattern'),
  schedules: z
    .array(
      z.object({
        weekday: z.number().min(0).max(6).optional(),
        start_time: z.string().min(1),
        end_time: z.string().min(1),
        date: z.string().optional(), // For specific dates
        rrule: z.string().optional(),
      })
    )
    .min(1, 'At least one schedule is required'),
  recurrence: z.object({
    type: z.string(),
    interval: z.number(),
    end: z.string(),
    count: z.number().optional(),
    until: z.string().optional(),
  }).default({
    type: 'weekly',
    interval: 1,
    end: 'never',
    count: 1,
    until: '',
  }),
  level: z.string().optional(),
  dress_code: z.string().optional(),
  category: z.string().optional(),
  status: z.enum(['active', 'inactive', 'draft', 'archived']).optional(),
  trial_available: z.boolean().optional(),
  is_active: z.boolean().optional(),
  waitlistEnabled: z.boolean().optional().default(true),
  payment_policy: z.enum(['immediate', 'flexible']).default('immediate'),
  payment_deadline_days: z.number().min(1).max(28).default(7),
});

// Export the type from the schema
export type ClassFormValues = z.infer<typeof ClassFormSchema>;

export interface ClassFormProps {
  onSubmit: (values: any) => void;
  onCancel: () => void;
  isSubmitting: boolean;
  defaultValues?: Partial<any>;
  locationOptions?: { value: string; label: string }[];
  seasonOptions?: { value: string; label: string }[];
}

export const ClassForm: React.FC<ClassFormProps> = ({
  onSubmit,
  onCancel,
  isSubmitting,
  defaultValues,
  locationOptions = [],
  seasonOptions = [],
}) => {
  // Convert price_cents to dollars for display
  const displayDefaults = defaultValues ? {
    ...defaultValues,
    price_dollars: defaultValues.price_cents !== undefined ? centsToDollars(defaultValues.price_cents) : '',
    location_id: defaultValues.location_id || 'none',
    season_id: defaultValues.season_id || 'no-season',
  } : undefined;

  const form = useForm<ClassFormValues>({
    resolver: zodResolver(ClassFormSchema),
    defaultValues: displayDefaults || {
      name: '',
      description: '',
      location_id: 'none',
      capacity: 1,
      season_id: 'no-season',
      billing_model: 'monthly',
      price_dollars: '',
      schedule_type: 'recurring_pattern',
      schedules: [
        { weekday: 1, start_time: '17:00', end_time: '18:00', rrule: '' },
      ],
      recurrence: {
        type: 'weekly',
        interval: 1,
        end: 'never',
        count: 1,
        until: '',
      },
      payment_policy: 'immediate',
      payment_deadline_days: 7,
    }
  });

  // On submit, convert price_dollars from dollars to cents
  const handleSubmit = form.handleSubmit((values) => {
    // Convert empty price to 0 cents, otherwise convert to cents
    const price_cents = values.price_dollars === '' ? 0 : dollarsToCents(values.price_dollars);
    const submitValues = { 
      ...values, 
      price_cents,
      location_id: values.location_id === 'none' ? '' : values.location_id,
      season_id: values.season_id === 'no-season' ? null : values.season_id,
    };
    delete submitValues.price_dollars;
    onSubmit(submitValues);
  });

  // Handle numeric input conversions
  const handleCapacityChange = (value: string) => {
    const numericValue = parseInt(value, 10);
    form.setValue('capacity', isNaN(numericValue) ? 1 : numericValue);
  };

  // Add billing model and price fields
  const billingModel = form.watch('billing_model');
  const trialAvailable = form.watch('trial_available');
  const isActive = form.watch('is_active');

  const priceLabel = {
    monthly: 'Monthly Price',
    per_session: 'Session/Term Price',
    weekly: 'Weekly Price',
    per_class: 'Per Class Price',
    annual: 'Annual Price',
  }[billingModel || 'monthly'];

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit} className="space-y-8">
        {/* General Information */}
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-blue-100">
                <Info className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <CardTitle className="text-lg">General Information</CardTitle>
                <p className="text-sm text-muted-foreground">Basic class details and description</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-sm font-medium flex items-center gap-2">
                  <BookOpen className="h-4 w-4" />
                  Class Name *
                </Label>
                <Input
                  id="name"
                  placeholder="Enter class name"
                  {...form.register('name', { required: true })}
                  className="h-11"
                />
                {form.formState.errors.name && (
                  <p className="text-sm text-destructive">{form.formState.errors.name.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="category" className="text-sm font-medium flex items-center gap-2">
                  <Tag className="h-4 w-4" />
                  Category
                </Label>
                <Input
                  id="category"
                  placeholder="e.g., Dance, Music, Art"
                  {...form.register('category')}
                  className="h-11"
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description" className="text-sm font-medium">Description</Label>
              <Textarea
                id="description"
                placeholder="Describe what this class is about, what students will learn, etc."
                {...form.register('description')}
                className="min-h-[100px] resize-none"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="level" className="text-sm font-medium flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Level
                </Label>
                <Input
                  id="level"
                  placeholder="e.g., Beginner, Intermediate, Advanced"
                  {...form.register('level')}
                  className="h-11"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="dress_code" className="text-sm font-medium flex items-center gap-2">
                  <Shirt className="h-4 w-4" />
                  Dress Code
                </Label>
                <Input
                  id="dress_code"
                  placeholder="What should students wear?"
                  {...form.register('dress_code')}
                  className="h-11"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Location & Season */}
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-purple-100">
                <MapPin className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Location & Season</CardTitle>
                <p className="text-sm text-muted-foreground">Where and when this class takes place</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="location" className="text-sm font-medium">Location</Label>
                <Select
                  value={form.watch('location_id') || 'none'}
                  onValueChange={(value) => form.setValue('location_id', value === 'none' ? '' : value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select a location" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select a location</SelectItem>
                    {locationOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="season" className="text-sm font-medium">Season (Optional)</Label>
                <Select
                  value={form.watch('season_id') || 'no-season'}
                  onValueChange={(value) => form.setValue('season_id', value === 'no-season' ? null : value)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="None (No Season)" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no-season">None (No Season)</SelectItem>
                    {seasonOptions.map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Optionally assign this class to a season. Classes without a season are always available.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Capacity & Pricing */}
        <Card className="border-l-4 border-l-green-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-green-100">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Capacity & Pricing</CardTitle>
                <p className="text-sm text-muted-foreground">Set enrollment limits and pricing structure</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <CapacityFields 
              form={form}
              handleCapacityChange={handleCapacityChange}
            />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              <div className="space-y-2">
                <Label htmlFor="billing_model" className="text-sm font-medium">Billing Model</Label>
                <Select
                  value={form.watch('billing_model') || 'monthly'}
                  onValueChange={(value) => form.setValue('billing_model', value as any)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select billing model" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Monthly</SelectItem>
                    <SelectItem value="per_session">Per Session/Term</SelectItem>
                    <SelectItem value="weekly">Weekly</SelectItem>
                    <SelectItem value="per_class">Per Class</SelectItem>
                    <SelectItem value="annual">Annual</SelectItem>
                  </SelectContent>
                </Select>
                {form.formState.errors.billing_model && (
                  <p className="text-sm text-destructive">{form.formState.errors.billing_model.message}</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="price" className="text-sm font-medium">{priceLabel} (USD)</Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="price"
                    type="number"
                    min={0}
                    step="0.01"
                    placeholder="0.00 (optional)"
                    {...form.register('price_dollars')}
                    className="h-11 pl-10"
                  />
                </div>
                {form.formState.errors.price_dollars && (
                  <p className="text-sm text-destructive">{form.formState.errors.price_dollars.message}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Payment Policy */}
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100">
                <CreditCard className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Payment Policy</CardTitle>
                <p className="text-sm text-muted-foreground">Configure enrollment payment requirements</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="payment_policy" className="text-sm font-medium">Payment Requirement</Label>
                <Select
                  value={form.watch('payment_policy') || 'immediate'}
                  onValueChange={(value) => form.setValue('payment_policy', value as any)}
                >
                  <SelectTrigger className="h-11">
                    <SelectValue placeholder="Select payment policy" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="immediate">
                      <div className="space-y-1">
                        <div className="font-medium">Payment Required to Enroll</div>
                        <div className="text-xs text-muted-foreground">Students must pay before enrollment is complete</div>
                      </div>
                    </SelectItem>
                    <SelectItem value="flexible">
                      <div className="space-y-1">
                        <div className="font-medium">Allow Flexible Payment</div>
                        <div className="text-xs text-muted-foreground">Students can enroll now and pay within deadline</div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {form.watch('payment_policy') === 'flexible' && (
                <div className="ml-6 space-y-2">
                  <Label htmlFor="payment_deadline_days" className="text-sm font-medium">Payment Due Within</Label>
                  <Select
                    value={form.watch('payment_deadline_days')?.toString() || '7'}
                    onValueChange={(value) => form.setValue('payment_deadline_days', parseInt(value))}
                  >
                    <SelectTrigger className="h-11">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="3">3 days</SelectItem>
                      <SelectItem value="7">7 days</SelectItem>
                      <SelectItem value="14">14 days</SelectItem>
                      <SelectItem value="30">30 days</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    Students will have this many days to complete payment after enrollment
                  </p>
                </div>
              )}
            </div>
            
            {form.watch('payment_policy') === 'immediate' && (
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle2 className="h-3 w-3 text-white" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-blue-900">Immediate Payment Benefits</p>
                    <ul className="text-xs text-blue-700 space-y-1">
                      <li>• Guaranteed payment before enrollment</li>
                      <li>• Reduced administrative overhead</li>
                      <li>• No unpaid accounts or follow-up required</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
            
            {form.watch('payment_policy') === 'flexible' && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <div className="h-5 w-5 rounded-full bg-amber-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Clock className="h-3 w-3 text-white" />
                  </div>
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-amber-900">Flexible Payment Considerations</p>
                    <ul className="text-xs text-amber-700 space-y-1">
                      <li>• Easier enrollment process for families</li>
                      <li>• Requires payment tracking and follow-up</li>
                      <li>• May result in some unpaid enrollments</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings & Status */}
        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-orange-100">
                <Settings className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Settings & Status</CardTitle>
                <p className="text-sm text-muted-foreground">Configure class workflow status and enrollment visibility</p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="status" className="text-sm font-medium">Workflow Status</Label>
              <Select
                value={form.watch('status') || 'active'}
                onValueChange={(value) => form.setValue('status', value as any)}
              >
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-yellow-400" />
                      Draft
                    </div>
                  </SelectItem>
                  <SelectItem value="active">
                    <div className="flex items-center gap-2">
                      <CheckCircle2 className="h-4 w-4 text-green-600" />
                      Active
                    </div>
                  </SelectItem>
                  <SelectItem value="inactive">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-gray-400" />
                      Inactive
                    </div>
                  </SelectItem>
                  <SelectItem value="archived">
                    <div className="flex items-center gap-2">
                      <div className="h-4 w-4 rounded-full bg-red-400" />
                      Archived
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Workflow status for internal management. Draft = Work in progress, Active = Ready for enrollment, Inactive = Temporarily unavailable, Archived = No longer offered.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center space-x-3 p-4 border rounded-lg bg-gray-50/50">
                <Checkbox
                  id="trial_available"
                  checked={trialAvailable || false}
                  onCheckedChange={(checked) => form.setValue('trial_available', !!checked)}
                />
                <div className="space-y-1">
                  <Label htmlFor="trial_available" className="text-sm font-medium cursor-pointer">
                    Allow Free Trial
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Students can try this class for free before enrolling
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3 p-4 border rounded-lg bg-gray-50/50">
                <Checkbox
                  id="is_active"
                  checked={isActive !== undefined ? isActive : true}
                  onCheckedChange={(checked) => form.setValue('is_active', !!checked)}
                />
                <div className="space-y-1">
                  <Label htmlFor="is_active" className="text-sm font-medium cursor-pointer">
                    Visible to Students
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Show this class to students in enrollment listings (independent of workflow status)
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Schedule */}
        <Card className="border-l-4 border-l-indigo-500">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-indigo-100">
                <Clock className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <CardTitle className="text-lg">Schedule</CardTitle>
                <p className="text-sm text-muted-foreground">Set up when this class meets</p>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <ScheduleFields form={form} />
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4 pt-6 border-t">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            <X className="h-4 w-4 mr-2" />
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[120px]">
            {isSubmitting ? (
              <>
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save className="h-4 w-4 mr-2" />
                Save Class
              </>
            )}
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
